// /procurement/board-returns/:id — build one shipment of failed boards.
//
// ── WHY EVERY EDIT IS A REQUEST ──────────────────────────────────────────────
// Nothing here is held in local state and flushed at the end. A tech scans boards
// into a draft on a phone at the flash station and finishes on a desktop, so the
// draft on the server IS the shared state. That requirement is the reason this
// page has no Save button: there is nothing to save, because it is already saved.
//
// The cost is a request per row, which is the right trade for a page whose rows
// arrive one scan at a time anyway.
//
// ── WHAT PRINTS, AND FROM WHERE ──────────────────────────────────────────────
// Labels go through printReturnLineLabels in utils/printing-utils, which owns
// every label geometry in this app. The return slip is a letter-size document and
// lives in returnSlip.ts beside this file, because it is not label stock.
//
// Both read line.label_identity. Neither derives it. That is the entire mechanism
// linking a board in the box to its row on the slip.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import BackLink from '../../components/BackLink';
import { useToast } from '../../components/Toast/ToastContext';
import HelpDrawer from '../../components/HelpDrawer';
import EntityCombobox, { type ComboboxOption } from '../../components/EntityCombobox';
import { LogoSpinner } from '../../components/ui/LogoSpinner';
import { modalLabel } from '../../components/review/fieldStyles';
import useUserStore from '../../stores/useUserStore';
import { printReturnLineLabels } from '../../utils/printing-utils';
import EntryBar from './EntryBar';
import LineTable from './LineTable';
import ReasonPassModal from './ReasonPassModal';
import { printReturnSlip } from './returnSlip';
import PageLayout from '../../components/ui/PageLayout';
import { buttonClass } from '../../components/ui/Button';
import { Field, fieldClass as kitFieldClass } from '../../components/ui/Field';
import { Tag } from '../../components/ui/Tag';
import Reveal from '../../components/ui/Reveal';
import { Appear } from '../../components/ui/Appear';
import { Tooltip } from '../../components/ui/Tooltip';
import {
  errorMessage,
  isQuantityLine,
  useBoardReturnsApi,
  type LookupHit,
  type LookupPartInstanceHit,
  type ManifestDetail,
  type ManifestLine,
  type ManifestStatus,
  type NormalizeProposal,
  type ReturnReason,
  type VendorOption,
} from './boardReturnsApi';


const STATUS_META: Record<ManifestStatus, { label: string; tone: 'accent' | 'info' | 'good' | 'neutral' }> = {
  draft: { label: 'Draft', tone: 'accent' },
  sent: { label: 'Sent', tone: 'info' },
  closed: { label: 'Closed', tone: 'good' },
  cancelled: { label: 'Cancelled', tone: 'neutral' },
};

const comboFieldClass = kitFieldClass();
const labelClass = modalLabel;

const BoardReturnManifest: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const toast = useToast();
  const brApi = useBoardReturnsApi();
  const user = useUserStore((s) => s.user);

  // Mirrors requireCapability('procurement', 'manufacturing') on the router.
  // Wider than the purchase-returns page on purpose: the person holding the dead
  // board is a flash-station tech.
  const canAct = !!(
    user?.isadmin || user?.can_admin || user?.can_procurement || user?.can_manufacturing
  );

  const [manifest, setManifest] = useState<ManifestDetail | null>(null);
  const [lines, setLines] = useState<ManifestLine[]>([]);
  const [reasons, setReasons] = useState<ReturnReason[]>([]);
  const [vendors, setVendors] = useState<VendorOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyLineId, setBusyLineId] = useState<string | null>(null);
  const [savingHeader, setSavingHeader] = useState(false);

  const [pass, setPass] = useState<{
    proposals: NormalizeProposal[];
    newReasons: { code: string; label: string; description: string | null }[];
    skipped: { n: number | undefined; why: string; code?: string }[];
  } | null>(null);
  const [passRunning, setPassRunning] = useState(false);
  const [applying, setApplying] = useState(false);
  const [togglingAll, setTogglingAll] = useState(false);

  /**
   * Ids that appeared on the most recent read.
   *
   * Diffed against the previous set rather than tracked at the add site, because
   * lines also arrive in bulk (a DOA burst) and from another person's session on
   * the same draft. Whatever is new to the LIST is what should announce itself.
   */
  const [landedIds, setLandedIds] = useState<Set<string>>(new Set());
  const knownIds = useRef<Set<string>>(new Set());
  // A separate flag rather than `knownIds.size > 0`. A manifest starts EMPTY, so
  // inferring "first read" from an empty set meant the first board ever added did
  // not animate — the one scan most in need of confirming.
  const hasRead = useRef(false);

  // Monotonic, and it guards every read that writes into `lines`. Without it a
  // GET issued before a delete and answering after it puts the deleted row back
  // on screen, and the next click on that row 404s.
  const readToken = useRef(0);

  const editable = manifest?.status === 'draft';

  // "Not set" is spelled as an option because it is a real answer here: a
  // manifest is built before anyone knows which vendor it ships to, and the
  // vendor stays clearable until it is sent.
  //
  // The stored vendor is carried explicitly for the same reason it always was:
  // the list leaves out disabled vendors, so a manifest whose vendor was
  // retired after the fact would otherwise read "Not set" while a vendor IS set.
  const vendorPickerOptions = useMemo<ComboboxOption[]>(() => {
    const opts: ComboboxOption[] = [{ id: '', label: 'Not set' }];
    const storedId = manifest?.vendor_id;
    if (storedId && !vendors.some((v) => v.id === storedId)) {
      opts.push({
        id: storedId,
        label: manifest?.vendor_name || 'Vendor no longer listed',
        chip: manifest?.vendor_code,
      });
    }
    for (const v of vendors) {
      opts.push({ id: v.id, label: v.name, chip: v.code });
    }
    return opts;
  }, [vendors, manifest?.vendor_id, manifest?.vendor_name, manifest?.vendor_code]);

  /**
   * Whatever is new since the last read. On the FIRST read everything is new, and
   * animating a full manifest on open would be motion for its own sake, so the
   * first pass only seeds the baseline.
   */
  const markLanded = useCallback((rows: ManifestLine[]) => {
    setLandedIds(hasRead.current
      ? new Set(rows.filter((l) => !knownIds.current.has(l.id)).map((l) => l.id))
      : new Set());
    knownIds.current = new Set(rows.map((l) => l.id));
    hasRead.current = true;
  }, []);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [detail, reasonList] = await Promise.all([
        brApi.getManifest(id),
        brApi.listReasons(),
      ]);
      setManifest(detail.manifest);
      setLines(detail.lines);
      markLanded(detail.lines);
      setReasons(reasonList.rows || []);
    } catch (err: any) {
      toast.error(err?.message || 'Could not load this manifest');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let live = true;
    brApi
      .listVendors()
      .then((list) => {
        if (live) setVendors(list);
      })
      .catch(() => {
        // A missing vendor list must not block scanning boards in. The vendor is
        // only required to SEND, and the select degrades to whatever is stored.
      });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Reload only the lines. Cheaper than the whole page after a row change. */
  const reloadLines = useCallback(async () => {
    if (!id) return;
    readToken.current += 1;
    const mine = readToken.current;
    const detail = await brApi.getManifest(id);
    // A read that has been overtaken is discarded rather than applied. Responses
    // do not have to come back in the order they were sent.
    if (mine !== readToken.current) return;
    setManifest(detail.manifest);
    setLines(detail.lines);
    markLanded(detail.lines);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // ── Entry ──────────────────────────────────────────────────────────────────

  const handleLookup = useCallback(
    async (input: { uid?: string; last4?: string; model_serial: string; board_type: string }) => {
      try {
        return await brApi.lookup(input);
      } catch (err: any) {
        toast.error(err?.message || 'Lookup failed');
        return null;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleAddBoard = useCallback(
    async (hit: LookupHit) => {
      if (!id) return false;

      // The server refuses this too. Catching it here turns a 409 into a sentence
      // that names where the board already is, before the request is spent.
      if (hit.already_on_manifest) {
        toast.error(
          `Already on ${hit.already_on_manifest.manifest_number}, line ${hit.already_on_manifest.line_number}.`,
        );
        return false;
      }

      try {
        const { line } = await brApi.addBoardLine(id, {
          board_id: hit.board.id,
          reason_id: hit.suggested_reason?.id ?? null,
          reason_raw: hit.suggested_reason_raw,
          in_print_list: true,
        });
        await reloadLines();
        toast.success(
          hit.suggested_reason
            ? `${line.label_identity} added, ${hit.suggested_reason.label.toLowerCase()}`
            : `${line.label_identity} added, needs a reason`,
        );
        return true;
      } catch (err: any) {
        toast.error(err?.message || 'Could not add that board');
        return false;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, reloadLines],
  );

  const handleAddPartInstance = useCallback(
    async (hit: LookupPartInstanceHit) => {
      if (!id) return false;

      // Same pre-check as a board. The lookup names no line number for a unit.
      if (hit.already_on_manifest) {
        toast.error(`Already on ${hit.already_on_manifest.manifest_number}.`);
        return false;
      }

      try {
        const { line } = await brApi.addPartInstanceLine(id, {
          part_instance_id: hit.instance.id,
          in_print_list: true,
        });
        await reloadLines();
        toast.success(`${line.label_identity} added, needs a reason`);
        return true;
      } catch (err: any) {
        toast.error(errorMessage(err, 'Could not add that unit'));
        return false;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, reloadLines],
  );

  const handleAddDoa = useCallback(
    async (input: {
      count: number;
      board_type: string;
      model_serial: string;
      reason_id: number | null;
      reason_raw: string | null;
      designator: string | null;
    }) => {
      if (!id) return false;
      try {
        const { lines: added } = await brApi.addDoaLines(id, { ...input, in_print_list: true });
        await reloadLines();
        toast.success(
          added.length === 1
            ? `${added[0].doa_number} added`
            : `${added.length} DOA rows added, ${added[0].doa_number} to ${added[added.length - 1].doa_number}`,
        );
        return true;
      } catch (err: any) {
        toast.error(err?.message || 'Could not add those rows');
        return false;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, reloadLines],
  );

  // ── Lines ──────────────────────────────────────────────────────────────────

  const handleUpdateLine = useCallback(
    async (lineId: string, patch: Parameters<typeof brApi.updateLine>[2]) => {
      if (!id) return;
      setBusyLineId(lineId);
      // Optimistic, because a print-list checkbox that waits on a round trip
      // feels broken when somebody is ticking twenty of them.
      setLines((prev) => prev.map((l) => (l.id === lineId ? { ...l, ...patch } as ManifestLine : l)));
      try {
        const { line } = await brApi.updateLine(id, lineId, patch);
        // Merge the server's row, then resolve the reason's display fields from
        // the vocabulary we already hold. NOT a reloadLines(): a full re-read here
        // meant that arrowing through the select fired one PATCH per keypress and
        // the reload made a WRONG intermediate value the one that stuck.
        const picked = patch.reason_id
          ? reasons.find((r) => r.id === patch.reason_id)
          : null;
        setLines((prev) => prev.map((l) => (l.id === lineId
          ? {
            ...l,
            ...line,
            reason_code: picked ? picked.code : (patch.reason_id === null ? null : l.reason_code),
            reason_label: picked ? picked.label : (patch.reason_id === null ? null : l.reason_label),
          }
          : l)));
      } catch (err: any) {
        toast.error(err?.message || 'That change did not save');
        await reloadLines();
      } finally {
        setBusyLineId(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, reloadLines, reasons],
  );

  const handleDeleteLine = useCallback(
    async (lineId: string) => {
      if (!id) return;
      setBusyLineId(lineId);
      try {
        await brApi.deleteLine(id, lineId);
        await reloadLines();
      } catch (err: any) {
        toast.error(err?.message || 'Could not remove that board');
      } finally {
        setBusyLineId(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, reloadLines],
  );

  const handleToggleAll = useCallback(
    async (next: boolean) => {
      // GUARDED, because the optimistic flip below makes the header checkbox read
      // as already-all-on, so a second click sends the opposite value and two
      // loops interleave over the same rows.
      if (!id || togglingAll) return;
      const changing = lines.filter((l) => l.in_print_list !== next);
      if (changing.length === 0) return;

      setTogglingAll(true);
      setLines((prev) => prev.map((l) => ({ ...l, in_print_list: next })));
      try {
        // Sequential rather than parallel: each PATCH opens a transaction that
        // locks the line and reads the manifest, and forty at once on a shop
        // handheld's connection is how a request times out mid-list.
        for (const line of changing) {
          // A row deleted while this runs would 404 and abort the loop, leaving
          // the rest of the list silently un-patched. Skip instead.
          // eslint-disable-next-line no-await-in-loop
          const stillThere = await brApi
            .updateLine(id, line.id, { in_print_list: next })
            .catch(() => null);
          if (stillThere === null) continue;
        }
      } catch (err: any) {
        toast.error(err?.message || 'Could not update the print list');
        await reloadLines();
      } finally {
        setTogglingAll(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, lines, reloadLines, togglingAll],
  );

  // ── Header ─────────────────────────────────────────────────────────────────

  const saveHeader = useCallback(
    async (patch: Parameters<typeof brApi.updateManifest>[1]) => {
      if (!id) return;
      setSavingHeader(true);
      try {
        const { manifest: next } = await brApi.updateManifest(id, patch);
        setManifest(next);
        if (patch.status === 'sent') toast.success(`${next.manifest_number} marked as sent`);
      } catch (err: any) {
        toast.error(err?.message || 'That change did not save');
        await reloadLines();
      } finally {
        setSavingHeader(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, reloadLines],
  );

  // ── Reason pass ────────────────────────────────────────────────────────────

  const runPass = async () => {
    if (!id) return;
    setPassRunning(true);
    try {
      const result = await brApi.normalize(id);
      setPass({
        proposals: result.proposals,
        newReasons: result.new_reasons,
        skipped: result.skipped,
      });
    } catch (err: any) {
      toast.error(err?.message || 'The reason pass did not run');
    } finally {
      setPassRunning(false);
    }
  };

  const applyPass = async (accepted: NormalizeProposal[]) => {
    if (!id) return;
    setApplying(true);
    try {
      const result = await brApi.applyNormalization(id, accepted);
      setPass(null);
      await load();
      const minted = result.minted.length > 0
        ? `, ${result.minted.length} new reason${result.minted.length === 1 ? '' : 's'} added`
        : '';
      toast.success(`${result.applied} coded${minted}`);
      // Said out loud. A line somebody else deleted, or one whose reason was
      // retired between propose and apply, otherwise just sat there still
      // reading "No reason yet" with no explanation.
      if (result.refused.length > 0) {
        toast.error(
          `${result.refused.length} line${result.refused.length === 1 ? '' : 's'} could not be coded. Run the pass again.`,
        );
      }
    } catch (err: any) {
      toast.error(err?.message || 'Could not apply those reasons');
    } finally {
      setApplying(false);
    }
  };

  // ── Printing ───────────────────────────────────────────────────────────────

  const printList = useMemo(() => lines.filter((l) => l.in_print_list), [lines]);

  const printLabels = () => {
    if (printList.length === 0) {
      toast.error('Nothing in the print list. Tick the boards you need labels for.');
      return;
    }
    printReturnLineLabels(
      printList.map((line) => ({
        // Verbatim, both rows. The identity is the server's string; the second
        // row prefers the CODE over the label because the code is what fits.
        line1: line.label_identity,
        // A stock label answers a different question. The sticker goes on a bag
        // or a box, not on one unit, so the second row says how many of what:
        // the COUNT leads, because printReturnLineLabels truncates at
        // FREE_TEXT_MAX_CHARS and the count is the half that must survive a long
        // name. SKU over base_name, for the same reason the board row prefers
        // the reason code: it is the shorter of the two.
        //
        // DESIGNATOR FIRST on a board row when there is one. The row truncates at
        // 19 characters and a reason code can itself be 19, so reason-first
        // silently dropped the designator — which is the half naming what to
        // look at.
        line2: isQuantityLine(line)
          ? `${line.quantity ?? 1} x ${line.sku || line.base_name || ''}`.trim()
          : line.designator
            ? `${line.designator} ${line.reason_code || ''}`.trim()
            : line.reason_code || line.reason_raw || '',
      })),
    );
  };

  const printSlip = () => {
    if (!manifest) return;
    if (lines.length === 0) {
      toast.error('Nothing on this manifest to put on a slip.');
      return;
    }
    const opened = printReturnSlip(manifest, lines);
    if (!opened) toast.error('Allow pop-ups to print the return slip.');
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  if (!canAct) {
    return (
      <PageLayout kind="focus" className="pt-10">
        <h1 className="text-xl font-semibold text-on-surface">Vendor RMA</h1>
        <p className="mt-2 text-sm text-on-surface-variant">
          This needs procurement or manufacturing access. Ask an admin to add it.
        </p>
      </PageLayout>
    );
  }

  if (loading) {
    return (
      <LogoSpinner label="Loading the manifest" />
    );
  }

  if (!manifest) {
    return (
      <PageLayout kind="focus" className="pt-10">
        <BackLink to="/procurement/board-returns" label="Vendor RMA" className="mb-2" />
        <h1 className="text-xl font-semibold text-on-surface">Manifest not found</h1>
      </PageLayout>
    );
  }

  const status = STATUS_META[manifest.status];
  const unreasoned = lines.filter((l) => !l.reason_id).length;
  const normalizable = lines.filter((l) => !l.reason_id && l.reason_raw).length;

  return (
    <PageLayout width="wide" kind="record" className="pt-6 text-on-surface">
      <header className="mb-5">
        <BackLink to="/procurement/board-returns" label="Vendor RMA" />
        <div className="mt-1 flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-semibold font-mono tracking-tight">
            {manifest.manifest_number}
          </h1>
          <Tag tone={status.tone}>{status.label}</Tag>
          <HelpDrawer title="Vendor RMA">
            <>
              <p>
                A manifest is one shipment of failed boards back to one vendor. It stays a draft
                while you build it, so you can scan boards in on a phone at the flash station and
                finish at a desk.
              </p>
              <p>
                <b className="text-on-surface">Four kinds of row.</b> A board with a record was
                detected and registered, so scanning it pulls in its flashing history and the reason
                it failed. A board that never flashed has no record anywhere, so it gets a DOA row
                and its number is its only identity. A tracked unit (GPS, LED, pyro) is one board
                found by its own sticker, with no flashing history to pull in. Stock is a count of
                a catalog part raised from a purchase order, shipping its return claim.
              </p>
              <p>
                <b className="text-on-surface">Sending retires what is on it.</b> Marking the
                manifest sent retires every board and tracked unit on it and takes it off its drone.
                Reopen the manifest if the carton did not leave, and that is undone. A closed
                manifest is final.
              </p>
              <p>
                <b className="text-on-surface">Serial lookup is mostly an AP thing.</b> ESC boards are
                not tied to drone serials, so the last-four route usually finds nothing for them.
                Scan the QR instead, or add them as DOA rows. GPS and LED units are found by
                scanning their own sticker; model and board type do not apply to them.
              </p>
              <p>
                <b className="text-on-surface">Reasons get coded.</b> Anything typed by hand stays
                exactly as typed, and the reason pass maps it onto a shared code so failures can be
                counted by board, vendor and month. New codes it proposes need one click to accept.
              </p>
              <p>
                <b className="text-on-surface">Labels and the slip agree.</b> The ID on a label is the
                same string in the slip's ID column, fixed when the row was added. It does not
                change afterwards, even if the board's drone serial does.
              </p>
            </>
          </HelpDrawer>
        </div>
      </header>

      {/* Header fields. Vendor is not required to build, only to send. */}
      {/* NOT a panel. Three equal slabs (header, entry bar, table) ranked the
          three equal, and they are not: this is setup you touch twice, so it sits
          on the page ground with a hairline under it and the entry bar keeps the
          only raised surface above the table. */}
      <section className="mb-5 pb-5 border-b border-black/25 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          {/* Every edit on this page is a request, so the picker only sends one
              when the answer actually changed — the same rule the text fields
              below apply on blur. */}
          <EntityCombobox
            id="manifest-vendor"
            label="Returning to"
            value={manifest.vendor_id || ''}
            options={vendorPickerOptions}
            onSelect={(o) => {
              const next = o && o.id ? String(o.id) : null;
              if (next !== (manifest.vendor_id || null)) saveHeader({ vendor_id: next });
            }}
            placeholder="Type to search"
            disabled={!editable || savingHeader}
            labelClassName={labelClass}
            inputClassName={comboFieldClass}
            emptyMessage="No vendor matches that."
            noOptionsMessage="No vendors to return to yet."
          />
        </div>

        <div>
          <label className={labelClass} htmlFor="manifest-rma">
            Their RMA number <span className="normal-case tracking-normal">(optional)</span>
          </label>
          <Field
            id="manifest-rma"
            defaultValue={manifest.rma_number || ''}
            maxLength={50}
            placeholder="If they have given you one"
            disabled={!editable || savingHeader}
            onBlur={(e) => {
              const next = e.target.value.trim() || null;
              if (next !== (manifest.rma_number || null)) saveHeader({ rma_number: next });
            }}
          />
          {/* The vendor usually issues this number while somebody is already
              holding the box, so the common case is printing without it. Say what
              that produces rather than leaving it to be discovered on paper. */}
          <Reveal show={!manifest.rma_number}>
            <p className="mt-1 text-xs text-on-surface-variant">
              Blank prints a line to write it on.
            </p>
          </Reveal>
        </div>

        <div className="sm:col-span-2">
          <label className={labelClass} htmlFor="manifest-notes">
            Notes for the slip
          </label>
          <Field
            id="manifest-notes"
            defaultValue={manifest.notes || ''}
            placeholder="Anything the vendor should read"
            disabled={!editable || savingHeader}
            onBlur={(e) => {
              const next = e.target.value.trim() || null;
              if (next !== (manifest.notes || null)) saveHeader({ notes: next });
            }}
          />
        </div>
      </section>

      <Reveal show={!!editable}>
        <div className="-m-1 p-1 pb-6">
          <EntryBar
            disabled={savingHeader}
            reasons={reasons}
            onLookup={handleLookup}
            onAddBoard={handleAddBoard}
            onAddPartInstance={handleAddPartInstance}
            onAddDoa={handleAddDoa}
          />
        </div>
      </Reveal>

      <LineTable
        lines={lines}
        reasons={reasons}
        editable={!!editable}
        busyLineId={busyLineId}
        togglingAll={togglingAll}
        landedIds={landedIds}
        onUpdate={handleUpdateLine}
        onDelete={handleDeleteLine}
        onToggleAll={handleToggleAll}
      />

      {/* ONE ACCENT. This row had two: gold on Print labels and green on Mark as
          sent, so nothing was primary. Gold is this app's primary-action color
          (Start a manifest uses it on the list page), and the commit is what
          deserves it. Printing is frequent but reversible, so it is a solid
          secondary; the two tools are ghosts. Frequency is not primacy. */}
      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={printLabels}
          disabled={printList.length === 0}
          className={buttonClass('tonal')}
        >
          Print {printList.length || ''} label{printList.length === 1 ? '' : 's'}
        </button>

        <button
          type="button"
          onClick={printSlip}
          disabled={lines.length === 0}
          className={buttonClass('quiet')}
        >
          Print return slip
        </button>

        <Appear show={!!editable}>
          <Tooltip
            label={
              normalizable === 0
                ? 'Every line with typed words already has a code'
                : `${normalizable} line${normalizable === 1 ? '' : 's'} to read`
            }
          >
            <button
              type="button"
              onClick={runPass}
              disabled={passRunning || normalizable === 0}
              className={buttonClass('quiet')}
            >
              {passRunning ? 'Reading reasons' : 'Process reasons'}
            </button>
          </Tooltip>
        </Appear>

        <Appear show={!!editable} className="ml-auto inline-flex">
            <Tooltip
              label={
                !manifest.vendor_id
                  ? 'Pick the vendor first'
                  : unreasoned > 0
                    ? `${unreasoned} line${unreasoned === 1 ? '' : 's'} still need a reason`
                    : 'Freeze this manifest and mark it shipped'
              }
            >
              <button
                type="button"
                onClick={() => saveHeader({ status: 'sent' })}
                disabled={savingHeader || lines.length === 0 || unreasoned > 0 || !manifest.vendor_id}
                className={buttonClass('filled')}
              >
                Mark as sent
              </button>
            </Tooltip>
        </Appear>

        <Appear show={manifest.status === 'sent'} className="ml-auto text-xs text-on-surface-variant">
          Sent. Lines are frozen; the print list still works.
        </Appear>
      </div>

      {pass && (
        <ReasonPassModal
          proposals={pass.proposals}
          newReasons={pass.newReasons}
          skipped={pass.skipped}
          applying={applying}
          onApply={applyPass}
          onClose={() => setPass(null)}
        />
      )}
    </PageLayout>
  );
};

export default BoardReturnManifest;
